import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons'
import {
  HeaderBackground,
  Navbar,
  Spinner,
  ErrorNotification,
  SuccessNotification,
} from '../components'
import { hasValue } from '../utils/formValidation'
import {
  getPovratProizvodaService,
  createPovratProizvodaService,
  updatePovratProizvodaService,
  deletePovratProizvodaService,
} from '../services/PovratProizvoda'

const emptyForm = { name: '', code: '', price: '' }

export default function PovratProizvodaForma() {
  const token = useSelector(state => state.authentication.token?.access_token)

  const [povratProizvodi, setPovratProizvodi] = useState([])
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState(null)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isForbidden, setIsForbidden] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const fetchPovratProizvoda = useCallback(async () => {
    setIsLoading(true)
    const result = await getPovratProizvodaService({ accessToken: token })
    if (result.isOk) setPovratProizvodi(result.data)
    else if (result.status === 403) setIsForbidden(true)
    else setErrorMessage(result.message)
    setIsLoading(false)
  }, [token])

  useEffect(() => {
    if (token) fetchPovratProizvoda()
  }, [token, fetchPovratProizvoda])

  const resetForm = () => {
    setForm(emptyForm)
    setEditingId(null)
    setErrors({})
  }

  const handleEdit = item => {
    setEditingId(item.id)
    setForm({
      name: item.name ?? '',
      code: item.code ?? '',
      price: item.price ?? '',
    })
    setErrors({})
  }

  const handleDelete = async id => {
    setIsProcessing(true)
    setErrorMessage(null)
    setSuccessMessage(null)

    const result = await deletePovratProizvodaService({
      id,
      accessToken: token,
    })
    if (result.isOk) {
      setSuccessMessage('Proizvod je uspješno obrisan.')
      if (editingId === id) resetForm()
      fetchPovratProizvoda()
    } else {
      setErrorMessage(result.message)
    }
    setIsProcessing(false)
  }

  const validate = () => {
    const newErrors = {}
    if (!hasValue(form.name)) newErrors.name = 'Naziv ne može biti prazan.'
    if (!hasValue(form.code)) newErrors.code = 'Šifra ne može biti prazna.'
    if (!hasValue(`${form.price}`))
      newErrors.price = 'Cijena ne može biti prazna.'
    setErrors(newErrors)
    return Object.values(newErrors).every(x => x == null)
  }

  const handleSubmit = async event => {
    event.preventDefault()
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!validate()) return

    setIsProcessing(true)
    const data = {
      name: form.name,
      code: form.code,
      price: Number(form.price),
      accessToken: token,
    }

    const result = editingId
      ? await updatePovratProizvodaService({ ...data, id: editingId })
      : await createPovratProizvodaService(data)

    if (result.isOk) {
      setSuccessMessage(
        editingId
          ? 'Proizvod je uspješno izmijenjen.'
          : 'Proizvod je uspješno dodan.',
      )
      resetForm()
      fetchPovratProizvoda()
    } else {
      setErrorMessage(result.message)
    }
    setIsProcessing(false)
  }

  if (!token) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda" />
        <div className="section">
          <div className="container">
            <p className="has-text-centered">
              Morate biti prijavljeni kako biste pristupili ovoj stranici.
            </p>
          </div>
        </div>
      </>
    )
  }

  if (isForbidden) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda - Forma" />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <HeaderBackground title="Povrat proizvoda - Forma" />
      <div className="section" id="povrat_proizvoda_forma">
        <div className="container">
          <Link to="/povrat-proizvoda" className="button mb-5">
            Povrat proizvoda
          </Link>
          <form onSubmit={handleSubmit}>
            <h2 className="subtitle">
              {editingId ? 'Izmjena proizvoda' : 'Dodavanje proizvoda'}
            </h2>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Naziv *</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="Unesite naziv..."
                  value={form.name}
                  onChange={event =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
              </div>
              {errors.name && (
                <span className="registerErrorSpan">{errors.name}</span>
              )}
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Šifra *</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="Unesite šifru..."
                  value={form.code}
                  onChange={event =>
                    setForm({ ...form, code: event.target.value })
                  }
                />
              </div>
              {errors.code && (
                <span className="registerErrorSpan">{errors.code}</span>
              )}
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Cijena *</label>
              <div className="control">
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Unesite cijenu..."
                  value={form.price}
                  onChange={event =>
                    setForm({ ...form, price: event.target.value })
                  }
                />
              </div>
              {errors.price && (
                <span className="registerErrorSpan">{errors.price}</span>
              )}
            </div>

            <div className="field">
              <button
                className="button greenButton mt-3"
                style={{ maxWidth: '400px' }}
                type="submit"
                disabled={isProcessing}>
                {editingId ? 'Sačuvaj izmjene' : 'Dodaj proizvod'}
              </button>
              {editingId && (
                <button
                  className="button ml-3 mt-3"
                  type="button"
                  style={{ maxWidth: '400px' }}
                  onClick={resetForm}
                  disabled={isProcessing}>
                  Otkaži
                </button>
              )}
            </div>
          </form>

          {errorMessage && <ErrorNotification errorMessage={errorMessage} />}
          {successMessage && (
            <SuccessNotification successMessage={successMessage} />
          )}

          <hr />

          <h2 className="subtitle">Lista proizvoda</h2>

          {isLoading && (
            <Spinner
              note="Procesiranje..."
              customStyle={{ marginTop: '2rem' }}
            />
          )}

          {!isLoading && povratProizvodi.length === 0 && (
            <p>Trenutno nema unesenih proizvoda.</p>
          )}

          {!isLoading && povratProizvodi.length > 0 && (
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th>Naziv</th>
                  <th>Šifra</th>
                  <th>Cijena</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {povratProizvodi.map(item => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.code}</td>
                    <td>{`${Number(item.price).toFixed(2)} KM`}</td>
                    <td>
                      <button
                        className="button is-small"
                        type="button"
                        onClick={() => handleEdit(item)}
                        disabled={isProcessing}>
                        <FontAwesomeIcon icon={faPen} />
                      </button>{' '}
                      <button
                        className="button is-small"
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        disabled={isProcessing}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}
